import { isAxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { payoutReviewErrorKey, type PayoutDecision, type ReviewQueueQuery } from './payout-review.contract';
import { payoutReviewService } from './payout-review.service';

const payoutReviewKeys = {
  all: ['moderator', 'payout-review'] as const,
  queue: (query: ReviewQueueQuery) => [...payoutReviewKeys.all, query] as const,
};

export const usePayoutReviewQueue = (query: ReviewQueueQuery) =>
  useQuery({
    queryKey: payoutReviewKeys.queue(query),
    queryFn: () => payoutReviewService.getQueue(query),
    placeholderData: (previous) => previous,
  });

export function usePayoutDecision() {
  const { t } = useApp();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const copy = t.moderatorPanel.payoutReview;

  return useMutation({
    mutationFn: ({ requestId, decision }: { requestId: number; decision: PayoutDecision }) =>
      payoutReviewService.decide(requestId, decision),
    onSuccess: (_result, { decision }) => {
      toast({ title: decision.action === 'APPROVED' ? copy.approvedToast : copy.rejectedToast });
    },
    onError: (error) => {
      const key = payoutReviewErrorKey(isAxiosError(error) ? error.response?.status : undefined);
      toast({ title: copy.errorTitle, description: copy[key], variant: 'destructive' });
    },
    // A 409 means someone else decided first: refetching shows the truth either way.
    onSettled: () => queryClient.invalidateQueries({ queryKey: payoutReviewKeys.all }),
  });
}
